/** @format */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { Chain, Environment } from '@shared/data-access/interfaces';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';

import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { ContainerId } from './../constants/container-id.constants';
import {
	GetRecommendationsDaoOptions,
	IRecommendationsResponse
} from './../interfaces/recommendations.interface';
import { ProductRecommendationsPageType } from './../constants/product-recommendations-page-type.constants';
import { WINDOW } from '@shared/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class ProductRecommendationsDao {
	private readonly cacheMap = new Map<string, Observable<IRecommendationsResponse>>();
	constructor(
		private chain: Chain,
		private environment: Environment,
		private http: HttpClient,
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) protected window: Window
	) {}

	/**
	 * @description
	 * Calls the ds-recs-details application with a specifically formatted request body and returns a list of recommended products
	 * with their associated details already mapped.
	 *
	 * @param requestBody -- The request body to send to the ds-recs-details microservice
	 * @param placementId -- The placement ID string of the type of recommendations to get back (e.g. others-you-may-like)
	 * @param maxNumberOfProductsToReturn -- The maximum number of recommended products to return
	 * @param project
	 * @param returnAllProductDetails -- Flag to determine whether to return all properties from Elastic
	 * @param returnOutOfStockProducts -- Flag to determine whether to return out-of-stock products in the recommendations list
	 * @param pageType -- The name of the page where recommendations are displayed
	 * @param containerID -- String identifier for the type of recs - (e.g. YMAL, PLA)
	 */
	getRecommendationsWithProductDetails$({
		requestBody,
		placementId,
		maxNumberOfProductsToReturn,
		project,
		returnAllProductDetails,
		returnOutOfStockProducts,
		pageType,
		containerID,
		dsgRecs
	}: GetRecommendationsDaoOptions): Observable<IRecommendationsResponse> {
		const cacheKey = JSON.stringify({
			requestBody,
			placementId,
			maxNumberOfProductsToReturn,
			project,
			returnAllProductDetails,
			returnOutOfStockProducts,
			pageType,
			containerID,
			dsgRecs
		});
		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Override default Timeout specified in HttpInterceptor
		httpHeaders = httpHeaders.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// For relevance team
		if (
			pageType === ProductRecommendationsPageType.CART ||
			pageType === ProductRecommendationsPageType.ORDER_CONFIRMATION ||
			containerID === ContainerId.YMAL ||
			containerID === ContainerId.PLA ||
			pageType === ProductRecommendationsPageType.ORDER_DETAILS ||
			containerID === ContainerId.FAVORITES_FBT
		) {
			const poolCSwimlane: string = this.getPoolCSwimlane();
			if (poolCSwimlane) {
				httpHeaders = httpHeaders.set(CustomHttpHeaders.POOL_C_SWIMLANE, poolCSwimlane);
			}
		}

		// Query String Parameters
		let httpParams = new HttpParams()
			.set('placementId', placementId)
			.set('storeId', this.chain.storeId)
			.set('maxNumberOfProductsToReturn', maxNumberOfProductsToReturn)
			.set('returnAllProductDetails', returnAllProductDetails)
			.set('returnOutOfStockProducts', returnOutOfStockProducts)
			.set('project', project);
		if (dsgRecs !== undefined) {
			httpParams = httpParams.set('flag', dsgRecs);
		}

		let cartContainerId = ContainerId.CART_FBT;
		let orderConfirmationContainerId = ContainerId.ORDERCONFIRM_FBT;
		switch (this.chain.chainIdentifierAbbr) {
			case 'pl':
				cartContainerId = ContainerId.PL_CART_FBT;
				orderConfirmationContainerId = ContainerId.PL_ORDERCONFIRM_FBT;
				break;
			case 'gg':
				cartContainerId = ContainerId.GG_CART_FBT;
				orderConfirmationContainerId = ContainerId.GG_ORDERCONFIRM_FBT;
				break;
		}

		// For relevance team
		switch (pageType) {
			case ProductRecommendationsPageType.CART:
				httpParams = httpParams.set(ContainerId.ID, cartContainerId);
				break;
			case ProductRecommendationsPageType.ORDER_CONFIRMATION:
				httpParams = httpParams.set(ContainerId.ID, orderConfirmationContainerId);
				break;
			case ProductRecommendationsPageType.ORDER_DETAILS:
				httpParams = httpParams.set(ContainerId.ID, ContainerId.ORDERHISTORY_FBT);
				break;
			case ProductRecommendationsPageType.PDP:
				if (containerID === ContainerId.YMAL) {
					httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_YMAL);
				} else if (containerID === ContainerId.PLA) {
					httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_PLA);
				}
				break;
			case ProductRecommendationsPageType.FAVORITES:
				httpParams = httpParams.set(ContainerId.ID, ContainerId.FAVORITES_FBT);
				break;
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		const endpoint = dsgRecs
			? this.environment.api.spring.microServiceProxy.recommendations.path.replace(
					'/v1/',
					'/v2/'
				)
			: this.environment.api.spring.microServiceProxy.recommendations.path;

		const cached$ = this.cacheMap.get(cacheKey);

		if (cached$) {
			return cached$;
		}

		// Make the HTTP Request
		const response = this.http
			.post<IRecommendationsResponse>(
				`${endpoint}recommendations/details`,
				requestBody,
				httpOptions
			)
			.pipe(shareReplay(1));

		this.cacheMap.set(cacheKey, response);

		return response;
	}

	public getPoolCSwimlane(): string | null {
		const swimLanes = this.window?._dsgTag?.customerLanes;
		if (swimLanes && swimLanes.length >= 3) {
			return String(swimLanes[2]);
		}
		return '';
	}
}
